/**
 * dailySummaryFetcher.service.ts
 *
 * Single-flight + TTL cache for the daily summary fetch pipeline.
 * Keyed by uid|date|mode — makes it impossible to run more than one
 * fetch for the same (uid, date, demoMode) at any point in time.
 *
 * All callers — AppContext, hooks, screens — funnel through here.
 * React render churn, Stripe resolving, ProtectedRoute re-renders:
 * none of it can produce duplicate Firestore reads.
 */

import { calculateDailyTDEE } from './tdee.service';
import { getDailyNutrition, getTargetRanges } from './nutrition.service';
import { getDailyBurn } from './workout.service';
import { getBodyMetrics } from './tdee.service';
import type { DailyNutrition, TargetRanges } from '../types/nutrition';
import type { DailyTDEE } from '../types/tdee';
import type { DailyBurn } from '../types/workout';

export interface RawDailySummaryData {
  tdee: DailyTDEE | null;
  nutrition: DailyNutrition | null;
  targetRanges: TargetRanges | null;
  bodyMetrics: { currentWeight: number; currentBodyFat?: number } | null;
  dailyBurn: DailyBurn | null;
}

const TTL_MS = 5000;

const inFlightByKey = new Map<string, Promise<RawDailySummaryData>>();
const cacheByKey = new Map<string, { at: number; value: RawDailySummaryData }>();

export async function fetchDailySummaryOnce(
  uid: string,
  date: string,
  isDemoMode: boolean,
): Promise<RawDailySummaryData> {
  const key = `${uid}|${date}|${isDemoMode ? 'demo' : 'live'}`;
  const now = Date.now();

  // TTL cache hit — return immediately
  const cached = cacheByKey.get(key);
  if (cached && now - cached.at < TTL_MS) {
    return cached.value;
  }

  // In-flight for this key — await the existing promise, no new fetch
  const existing = inFlightByKey.get(key);
  if (existing) {
    return existing;
  }

  const fetchUid = isDemoMode ? 'demo-user' : uid;

  const p = (async (): Promise<RawDailySummaryData> => {
    try {
      const [nutrition, targetRanges, metrics, burn, tdee] = await Promise.all([
        getDailyNutrition(fetchUid, date).catch((err) => {
          console.error('[DailySummaryFetcher] getDailyNutrition failed:', err);
          return null;
        }),
        getTargetRanges(fetchUid).catch(() => null),
        getBodyMetrics(fetchUid).catch(() => null),
        getDailyBurn(fetchUid, date).catch(() => null),
        calculateDailyTDEE(fetchUid, date).catch((err) => {
          console.error('[DailySummaryFetcher] calculateDailyTDEE failed:', err);
          return null;
        }),
      ]);

      const value: RawDailySummaryData = {
        tdee,
        nutrition,
        targetRanges,
        bodyMetrics: metrics
          ? { currentWeight: metrics.currentWeight, currentBodyFat: metrics.currentBodyFat }
          : null,
        dailyBurn: burn,
      };

      cacheByKey.set(key, { at: Date.now(), value });
      return value;
    } finally {
      inFlightByKey.delete(key);
    }
  })();

  inFlightByKey.set(key, p);
  return p;
}

/**
 * Bust the cache for a specific uid/date (call after saves/deletes).
 * The next call to fetchDailySummaryOnce will hit Firestore fresh.
 */
export function invalidateFetcherCache(uid?: string, date?: string): void {
  if (uid && date) {
    // Targeted invalidation — only bust the specific key
    cacheByKey.delete(`${uid}|${date}|live`);
    cacheByKey.delete(`${uid}|${date}|demo`);
    inFlightByKey.delete(`${uid}|${date}|live`);
    inFlightByKey.delete(`${uid}|${date}|demo`);
  } else {
    // Full invalidation (e.g. date rollover, logout)
    cacheByKey.clear();
    inFlightByKey.clear();
  }
}
